/**
 * The frame v2 note header's three visit facts: room, start, reason for visit (PROD-3045 N4).
 *
 * THE DEFECT (measured on dev, 6 Oct 18:50, one visit, two roles):
 *   physician  "ROOM FOR WALK-IN / 6 Oct 2026, 15:15"            — no reason;
 *   nurse      "ROOM / Room For Walk-In · REASON FOR VISIT / Week 1" — no time.
 * It looked like a role rule, but it was the PATH. Each fact had one source that only one path had:
 *   - the start came only from tap-time router state, which a deep link (the nurse) does not carry;
 *   - the reason came only from the by-appointment encounter query, which is disabled once an
 *     encounter id is known, so the clinician who opened (and created) the visit never got it.
 *
 * THE RULE: read each fact from a source EVERY path loads, the appointment record
 * (`GET /api/Appointment/GetById`, `useAppointmentStatus`). That is also Flutter's header
 * (`add_encounter_detail_widget.dart:266-280`: `appointment.room.name`, `appointment.start`,
 * `appointment.reasonCode.display`).
 *   - Once the record has LOADED it is the only source, for every fact. A copy never fills a gap in a
 *     loaded record: a reason cleared on the appointment must not come back from the worklist row or
 *     the encounter, and a rescheduled start must not stay at the old time for whoever tapped the
 *     row (the fetched appointment wins, as the status pill's PROD-2030 rule says). Both paths load
 *     the record, so both read the same.
 *   - While it LOADS (`undefined`), or when there is none (`null`), the copies of the same fields fill
 *     in: the start from tap-time router state; the reason from the tapped row's `reasonCode.display`,
 *     then the encounter's `reasonForVisitName`. No copy carries a room NAME (the lists ship a bare
 *     roomId), so the room waits for the record.
 * Nothing is invented: a fact no source carries stays undefined and its cell does not draw.
 */
export interface VisitHeaderFacts {
  room?: string;
  startISO?: string;
  reason?: string;
}

const text = (v: string | null | undefined): string | undefined => v?.trim() || undefined;

export function visitHeaderFacts(src: {
  /** The appointment record (`useAppointmentStatus` data): `undefined` while loading, `null` when none. */
  appointment?: {
    room?: { name?: string | null } | null;
    start?: string | null;
    reason?: string | null;
  } | null;
  /** Tap-time router state: `nav.appointmentStart`. */
  navStartISO?: string | null;
  /** Tap-time router state: `nav.createContext.reasonForVisitName` (the row's reasonCode.display). */
  navReason?: string | null;
  /** The fetched encounter's copy: `reasonForVisitName`. */
  encounterReason?: string | null;
}): VisitHeaderFacts {
  const record = src.appointment;
  const room = text(record?.room?.name);
  const startISO = record ? text(record.start) : text(src.navStartISO);
  const reason = record ? text(record.reason) : (text(src.navReason) ?? text(src.encounterReason));
  return {
    ...(room ? { room } : {}),
    ...(startISO ? { startISO } : {}),
    ...(reason ? { reason } : {}),
  };
}
